import { useState, useEffect } from 'react';
import { MapPinIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { getCurrentLocation, isWithinDeliveryRadius } from '../utils/location';

const LocationCheck = ({ restaurantLocation, onLocationVerified }) => {
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkDeliveryAvailability();
  }, []);

  const checkDeliveryAvailability = async () => {
    try {
      setLoading(true);
      setError(null);

      const location = await getCurrentLocation();
      setUserLocation(location);

      if (restaurantLocation) {
        const result = isWithinDeliveryRadius(
          restaurantLocation.latitude,
          restaurantLocation.longitude,
          location.latitude,
          location.longitude
        );

        setDeliveryStatus(result);
        onLocationVerified(result.deliveryAvailable, result.distance, result.deliveryFee, result.zone);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const retryLocationCheck = () => {
    checkDeliveryAvailability();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-food-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Checking your location...
          </h2>
          <p className="text-gray-600">
            We need to verify if delivery is available in your area
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Location Access Required
          </h2>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <button
            onClick={retryLocationCheck}
            className="w-full bg-food-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-food-600 transition-colors"
          >
            Try Again
          </button>
          <p className="text-sm text-gray-500 mt-4">
            Please enable location access in your browser settings
          </p>
        </div>
      </div>
    );
  }

  if (deliveryStatus && !deliveryStatus.deliveryAvailable) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <MapPinIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Too Far for Delivery
          </h2>
          <p className="text-gray-600 mb-4">
            Sorry, we don't deliver to your location. You are {deliveryStatus.distance} km away from our restaurant.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            We currently deliver within {deliveryStatus.maxRadius} km radius
          </p>
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-blue-800 font-medium">Delivery Zones:</p>
            <p className="text-xs text-blue-600 mt-1">• 0-4 km: ₹10 delivery charge</p>
            <p className="text-xs text-blue-600">• 4-10 km: ₹20 delivery charge</p>
          </div>
          <button
            onClick={retryLocationCheck}
            className="w-full bg-gray-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors"
          >
            Check Again
          </button>
        </div>
      </div>
    );
  }

  if (deliveryStatus && deliveryStatus.deliveryAvailable) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <MapPinIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Delivery Available!
          </h2>
          <p className="text-gray-600 mb-4">
            Great! We deliver to your location. You are {deliveryStatus.distance} km away.
          </p>
          <div className="bg-green-50 p-4 rounded-lg mb-6">
            <p className="text-lg font-bold text-green-800">
              Delivery Charge: ₹{deliveryStatus.deliveryFee}
            </p>
            <p className="text-sm text-green-600 mt-1">
              {deliveryStatus.zone.name} ({deliveryStatus.distance} km)
            </p>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Delivery zones: 0-4km (₹10) • 4-10km (₹20)
          </p>
          <button
            onClick={() => window.location.href = '/menu'}
            className="w-full bg-green-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-600 transition-colors"
          >
            Continue to Menu
          </button>
        </div>
      </div>
    );
  }

  // Location verified and within radius - this component will be hidden
  return null;
};

export default LocationCheck;